/*
 * Manually created type definitions for the Google PAL SDK
 * API reference: https://developers.google.com/ad-manager/pal/html5/reference/js
 */

/**
 * Various settings to the PAL SDK related to privacy and regulations. The settings
 * can help make decisions about the choice regarding the identifiers used while
 * generating nonce signals.
 */
export type ConsentSettings = {
  /**
   * Whether PAL has purpose 1 consent to use identifiers and storage. The value
   * needs to be determined by integrating with CMP (Consent Management Provider).
   * This is set to false by default. If specified as true, PAL has consent from
   * the user to use information like cookies, device IDs, and advertising IDs.
   * This value needs to be set to be IAB TCFv2 (Transparency and Consent
   * Framework) compliant.
   */
  allowStorage?: boolean
}

/**
 * The programmatic access library allows publishers to collect signals that can
 * be used to unlock AdX demand. Publishers should create a NonceLoader on page
 * load in order to reduce latency. For each stream (for DAI) or individual ad
 * request, call the relevant setters as needed followed by a call to
 * loadNonceManager. Publishers can then call NonceManager.getNonce() in order to
 * get the string that should be added to ad requests as the value to the "&paln"
 * URL parameter. Publishers should then call NonceManager.sendAdClick() and
 * NonceManager.sendAdImpression() as appropriate.
 */
export type NonceLoader = {
  loadNonceManager: (request: NonceRequest) => Promise<NonceManager>
}

/**
 * Manages a nonce and its event reporting for a single content playback session.
 * The content playback session lifecycle methods, including sendAdClick,
 * sendAdTouch, sendPlaybackStart, and sendPlaybackEnd must be called as these
 * respective events occur in order to enable programmatic monetization.
 */
export type NonceManager = {
  getNonce: () => string
  sendAdClick: () => void
  /** @deprecated */ sendAdImpression: () => void
  sendAdTouch: (clickOrTouchEvent: Event) => void
  sendPlaybackEnd: () => void
  sendPlaybackStart: () => void
}

/**
 * The NonceRequest is used to store information about the request for a nonce.
 */
export type NonceRequest = {
  /**
   * Set to true if the ad will be auto played without waiting for user interaction.
   * Set to false if the ad will wait for user interaction before being played.
   */
  adWillAutoPlay: boolean | null

  /**
   * Set to true if the ad will be played while muted. Set to false if the ad will
   * play unmuted.
   */
  adWillPlayMuted: boolean | null

  /**
   * Set to true if the player intends to continuously play the content videos one
   * after another similar to TV broadcast or video playlist. Set to false
   * otherwise.
   */
  continuousPlayback: boolean | null

  /**
   * The description URL of the content during which the ad will be played. Any
   * description URL string longer than 500 characters will be ignored and
   * excluded from the nonce.
   */
  descriptionUrl: string

  /**
   * Whether VAST icons are supported by the video player.
   */
  iconsSupported: boolean

  /**
   * The length limit of the generated nonce. The loadNonceManager promise will be
   * rejected if the nonce is longer than this limit. Defaults to the largest
   * possible integer. Choosing a shorter length limit may cause various targeting
   * properties to be excluded from the nonce.
   */
  nonceLengthLimit: number

  /**
   * The name of the partner integrating OMID measurement. Any partner name string
   * longer than 200 characters will be ignored and excluded from the nonce.
   *
   * This must match the name supplied to the OM SDK for the ad session. For further
   * details, see the OM SDK Onboarding Guide.
   *
   * To successfully include the omid_p= value in a nonce, both this omidPartnerName
   * and omidPartnerVersion must be set.
   *
   * @see https://omsdk-demo-files.s3-us-west-2.amazonaws.com/docs/WebVideo/Onboarding%2BGuide%2Bfor%2BIntegrators.pdf
   */
  omidPartnerName: string

  /**
   * The version of the partner integrating OMID measurement. Any partner version
   * string longer than 200 characters will be ignored and excluded from the nonce.
   *
   * This must match the string supplied to the OM SDK for the ad session.
   *
   * To successfully include the omid_p= value in a nonce, both this
   * omidPartnerVersion and omidPartnerName must be set.
   */
  omidPartnerVersion: string

  /**
   * The version of OMID that the player responsible for ad playback integrates
   * with. Any OMID version string longer than 200 characters will be ignored and
   * excluded from the nonce.
   *
   * This must be set to the version provided by the OM SDK's getVersion() method.
   * For further details, see the OMID API docs.
   *
   * @see https://s3-us-west-2.amazonaws.com/omsdk-demo-files/docs/OMID_API_v1.2.pdf
   */
  omidVersion: string

  /**
   * Sets the partner provided player type. This should be used to specify the name
   * of the partner player. Any player type string longer than 200 characters will
   * be ignored and excluded from the nonce.
   */
  playerType: string

  /**
   * Sets the partner provided player version. This should be used to specify the
   * version of the partner player. Any player version string longer than 200
   * characters will be ignored and excluded from the nonce.
   */
  playerVersion: string

  /**
   * The publisher provided ID. Any PPID longer than 200 characters will be ignored
   * and excluded from the nonce.
   */
  ppid: string

  /**
   * The session ID is a temporary random ID. It is used exclusively for frequency
   * capping on connected TVs (smart TVs, game consoles, and set-top-boxes). A
   * session ID must be a UUID.
   */
  sessionId: string

  /**
   * A comma-separated list of supported frameworks. These values are defined in
   * the AdCOM 1.0 "API Frameworks" list. Example: "2,7,9" indicates this player
   * supports VPAID 2.0, OMID 1.0, and SIMID 1.1.
   *
   * 1	VPAID 1.0
   * 2	VPAID 2.0
   * 3	MRAID 1.0
   * 4	ORMMA
   * 5	MRAID 2.0
   * 6	MRAID 3.0
   * 7	OMID 1.0
   * 8	SIMID 1.0
   * 9	SIMID 1.1
   * 500+	Vendor-specific codes.
   *
   * @see https://github.com/InteractiveAdvertisingBureau/AdCOM/blob/main/AdCOM%20v1.0%20FINAL.md#list_apiframeworks
   */
  supportedApiFrameworks: string | number[]

  /**
   * The URL to which the ads will be targeted. By default, this is the same as the
   * page that will display the ads, but it may be manually set.
   */
  url: string

  /**
   * The height of the ad video element.
   */
  videoHeight: number

  /**
   * The width of the ad video element.
   */
  videoWidth: number
}

/**
 * The Programmatic Access Libraries (PAL) are lightweight SDKs that provide
 * discrete access to targeting signals for Google Ad Manager programmatic ads.
 */
export type PalSdk = {
  ConsentSettings: new () => ConsentSettings
  NonceLoader: new (settings: ConsentSettings) => NonceLoader
  NonceRequest: new () => NonceRequest
}

export type Goog = {
  pal: PalSdk
}

export interface PlaybackParams {
  url: string
  sessionId: string
  videoHeight?: number
  videoWidth?: number
}

export const EMPTY_PLAYBACK_PARAMS: PlaybackParams = {
  url: '',
  sessionId: '',
  videoHeight: 0,
  videoWidth: 0,
}
